
import { supabase } from "@/integrations/supabase/client";
import { 
  CommissionOffer,
  CommissionRequest,
  CommissionMessage,
  CommissionDelivery
} from "@/types/commission.types";
import { toast } from "sonner";

// Commission Offers
export const getCommissionOffers = async (): Promise<CommissionOffer[]> => {
  try {
    console.log("Service: Fetching commission offers...");
    
    // Updated query to not use foreign key relationship but do a separate join
    const { data: offersData, error: offersError } = await supabase
      .from('commission_offers')
      .select('*')
      .eq('is_active', true)
      .order('created_at', { ascending: false });

    if (offersError) {
      console.error('Erro na consulta de comissões:', offersError);
      throw offersError;
    }
    
    console.log("Service: Found offers:", offersData?.length || 0);
    
    // For each offer, fetch the artist profile data
    const offers = await Promise.all((offersData || []).map(async (offer) => {
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('username, avatar_url')
        .eq('id', offer.artist_id)
        .single();
        
      if (profileError) {
        console.error('Erro ao buscar perfil do artista:', profileError);
        return {
          ...offer,
          artist: { username: '', avatar_url: '' }
        } as CommissionOffer;
      }
      
      return {
        ...offer,
        artist: profileData ? { 
          username: profileData.username || '', 
          avatar_url: profileData.avatar_url || '' 
        } : { username: '', avatar_url: '' }
      } as CommissionOffer;
    }));
    
    console.log("Service: Processed offers with artist data:", offers.length);
    return offers;
  } catch (error) {
    console.error('Error fetching commission offers:', error);
    toast.error('Erro ao carregar ofertas de comissões');
    return [];
  }
};

export const getArtistCommissionOffers = async (artistId: string): Promise<CommissionOffer[]> => {
  try {
    console.log("Service: Buscando comissões para o artista:", artistId);
    
    // Updated query to not use foreign key relationship but do a separate join
    const { data: offersData, error: offersError } = await supabase
      .from('commission_offers')
      .select('*')
      .eq('artist_id', artistId)
      .order('created_at', { ascending: false });

    if (offersError) {
      console.error('Erro na consulta de artista:', offersError);
      throw offersError;
    }
    
    // Get artist profile in a separate query
    const { data: profileData, error: profileError } = await supabase
      .from('profiles')
      .select('username, avatar_url')
      .eq('id', artistId)
      .single();
      
    if (profileError) {
      console.error('Erro ao buscar perfil do artista:', profileError);
    }
    
    const artistProfile = profileError ? { username: '', avatar_url: '' } : 
      { username: profileData?.username || '', avatar_url: profileData?.avatar_url || '' };
    
    console.log("Service: Comissões do artista:", offersData);
    
    // Combine offer data with artist profile
    const offers = (offersData || []).map(offer => ({
      ...offer,
      artist: artistProfile
    })) as CommissionOffer[];
    
    return offers;
  } catch (error) {
    console.error('Error fetching artist commission offers:', error);
    toast.error('Erro ao carregar ofertas de comissões do artista');
    return [];
  }
};

export const getCommissionOfferById = async (id: string): Promise<CommissionOffer | null> => {
  try {
    console.log("Service: Fetching commission offer by ID:", id);
    
    if (!id || id.trim() === '') {
      console.warn("Service: No valid ID provided");
      return null;
    }

    // Get commission offer data
    const { data: offerData, error: offerError } = await supabase
      .from('commission_offers')
      .select('*')
      .eq('id', id)
      .single();

    console.log("Service: Offer query result:", { offerData, offerError });

    if (offerError) {
      console.error('Service: Error fetching offer:', offerError);
      if (offerError.code === 'PGRST116') {
        console.warn("Service: No offer found with ID:", id);
        return null;
      }
      throw offerError;
    }
    
    if (!offerData) {
      console.warn("Service: No offer data returned for ID:", id);
      return null;
    }
    
    // Get artist profile in a separate query
    const { data: profileData, error: profileError } = await supabase
      .from('profiles')
      .select('username, avatar_url')
      .eq('id', offerData.artist_id)
      .single();
      
    if (profileError) {
      console.error('Erro ao buscar perfil do artista:', profileError);
    }
    
    // Combine offer data with artist profile
    const result = {
      ...offerData,
      artist: profileError ? { username: '', avatar_url: '' } : 
        { username: profileData?.username || '', avatar_url: profileData?.avatar_url || '' }
    } as CommissionOffer;
    
    console.log("Service: Returning commission offer:", result);
    return result;
  } catch (error) {
    console.error('Error fetching commission offer:', error);
    throw error;
  }
};

export const createCommissionOffer = async (offer: Omit<CommissionOffer, 'id' | 'created_at' | 'updated_at' | 'artist'>): Promise<CommissionOffer | null> => {
  try {
    console.log("Criando nova oferta de comissão:", offer);
    
    const { data, error } = await supabase
      .from('commission_offers')
      .insert(offer)
      .select()
      .single();

    if (error) {
      console.error('Erro ao inserir comissão:', error);
      throw error;
    }
    
    console.log("Comissão criada:", data);
    toast.success('Oferta de comissão criada com sucesso');
    return data;
  } catch (error) {
    console.error('Error creating commission offer:', error);
    toast.error('Erro ao criar oferta de comissão');
    return null;
  }
};

export const updateCommissionOffer = async (id: string, offer: Partial<CommissionOffer>): Promise<CommissionOffer | null> => {
  try {
    const { data, error } = await supabase
      .from('commission_offers')
      .update({
        ...offer,
        updated_at: new Date().toISOString()
      })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    toast.success('Oferta de comissão atualizada com sucesso');
    return data;
  } catch (error) {
    console.error('Error updating commission offer:', error);
    toast.error('Erro ao atualizar oferta de comissão');
    return null;
  }
};

export const deleteCommissionOffer = async (id: string): Promise<boolean> => {
  try {
    const { error } = await supabase
      .from('commission_offers')
      .delete()
      .eq('id', id);

    if (error) throw error;
    toast.success('Oferta de comissão excluída com sucesso');
    return true;
  } catch (error) {
    console.error('Error deleting commission offer:', error);
    toast.error('Erro ao excluir oferta de comissão');
    return false;
  }
};

// Commission Requests
export const getCommissionRequests = async (userId: string): Promise<CommissionRequest[]> => {
  try {
    const { data, error } = await supabase
      .from('commission_requests')
      .select('*')
      .or(`client_id.eq.${userId},artist_id.eq.${userId}`)
      .order('created_at', { ascending: false });

    if (error) throw error;
    
    // Cast the status to the expected type in a type-safe way
    return (data || []).map(request => ({
      ...request,
      status: request.status as "pending" | "accepted" | "in_progress" | "revision" | "completed" | "rejected"
    }));
  } catch (error) {
    console.error('Error fetching commission requests:', error);
    toast.error('Erro ao carregar solicitações de comissões');
    return [];
  }
};

// Nova função para buscar apenas as comissões contratadas por um cliente específico
export const getClientCommissionRequests = async (clientId: string): Promise<CommissionRequest[]> => {
  try {
    if (!clientId) return [];
    
    const { data, error } = await supabase
      .from('commission_requests')
      .select('*')
      .eq('client_id', clientId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    
    // Cast the status to the expected type in a type-safe way
    return (data || []).map(request => ({
      ...request,
      status: request.status as "pending" | "accepted" | "in_progress" | "revision" | "completed" | "rejected"
    }));
  } catch (error) {
    console.error('Error fetching client commission requests:', error);
    toast.error('Erro ao carregar comissões contratadas');
    return [];
  }
};

export const getCommissionRequestById = async (id: string): Promise<CommissionRequest | null> => {
  try {
    console.log("Service: Starting fetch for commission request ID:", id);
    
    if (!id || id.trim() === '') {
      console.warn("Service: No valid ID provided");
      return null;
    }

    // First, let's check if the record exists at all
    const { data, error, count } = await supabase
      .from('commission_requests')
      .select('*', { count: 'exact' })
      .eq('id', id)
      .single();

    console.log("Service: Query result - data:", data, "error:", error, "count:", count);

    if (error) {
      console.error("Service: Supabase error:", error);
      if (error.code === 'PGRST116') {
        console.warn("Service: No rows returned - commission request not found");
        return null;
      }
      throw error;
    }
    
    if (!data) {
      console.warn("Service: No data returned for ID:", id);
      return null;
    }
    
    // Cast the status to the expected type
    const result = {
      ...data, 
      status: data.status as "pending" | "accepted" | "in_progress" | "revision" | "completed" | "rejected"
    };
    
    console.log("Service: Successfully returning commission request:", result);
    return result;
  } catch (error) {
    console.error('Service: Error fetching commission request:', error);
    throw error;
  }
};

export const createCommissionRequest = async (request: Omit<CommissionRequest, 'id' | 'created_at' | 'updated_at'>): Promise<CommissionRequest | null> => {
  try {
    console.log("Service: Creating commission request:", request);
    
    const { data, error } = await supabase
      .from('commission_requests')
      .insert(request)
      .select()
      .single();

    if (error) {
      console.error("Service: Error creating commission request:", error);
      throw error;
    }
    
    console.log("Service: Commission request created successfully:", data);
    toast.success('Solicitação de comissão enviada com sucesso');
    
    // Cast the status to the expected type
    return data ? {
      ...data,
      status: data.status as "pending" | "accepted" | "in_progress" | "revision" | "completed" | "rejected"
    } : null;
  } catch (error) {
    console.error('Error creating commission request:', error);
    toast.error('Erro ao criar solicitação de comissão');
    return null;
  }
};

export const updateCommissionRequest = async (id: string, request: Partial<CommissionRequest>): Promise<CommissionRequest | null> => {
  try {
    const { data, error } = await supabase
      .from('commission_requests')
      .update({
        ...request,
        updated_at: new Date().toISOString()
      })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    toast.success('Solicitação de comissão atualizada com sucesso');
    
    // Cast the status to the expected type
    return data ? {
      ...data,
      status: data.status as "pending" | "accepted" | "in_progress" | "revision" | "completed" | "rejected"
    } : null;
  } catch (error) {
    console.error('Error updating commission request:', error);
    toast.error('Erro ao atualizar solicitação de comissão');
    return null;
  }
};

// Commission Messages
export const getCommissionMessages = async (requestId: string): Promise<CommissionMessage[]> => {
  try {
    const { data, error } = await supabase
      .from('commission_messages')
      .select(`
        *,
        sender:profiles(username, avatar_url)
      `)
      .eq('commission_request_id', requestId)
      .order('created_at', { ascending: true });

    if (error) throw error;
    
    // Proper type-safe conversion
    return (data || []).map(item => {
      const senderData = item.sender as any;
      return {
        ...item,
        sender: senderData && typeof senderData === 'object'
          ? {
              username: senderData.username || '',
              avatar_url: senderData.avatar_url || ''
            }
          : { username: '', avatar_url: '' }
      } as CommissionMessage;
    });
  } catch (error) {
    console.error('Error fetching commission messages:', error);
    toast.error('Erro ao carregar mensagens da comissão');
    return [];
  }
};

export const sendCommissionMessage = async (message: Omit<CommissionMessage, 'id' | 'created_at' | 'sender'>): Promise<CommissionMessage | null> => {
  try {
    const { data, error } = await supabase
      .from('commission_messages')
      .insert(message)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error sending commission message:', error);
    toast.error('Erro ao enviar mensagem');
    return null;
  }
};

export const markMessagesAsRead = async (requestId: string, userId: string): Promise<boolean> => {
  try {
    const { error } = await supabase
      .from('commission_messages')
      .update({ read: true })
      .eq('commission_request_id', requestId)
      .neq('sender_id', userId);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error marking messages as read:', error);
    return false;
  }
};

// Commission Deliveries
export const getCommissionDeliveries = async (requestId: string): Promise<CommissionDelivery[]> => {
  try {
    const { data, error } = await supabase
      .from('commission_deliveries')
      .select('*')
      .eq('commission_request_id', requestId)
      .order('created_at', { ascending: true });

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching commission deliveries:', error);
    toast.error('Erro ao carregar entregas da comissão');
    return [];
  }
};

export const createCommissionDelivery = async (delivery: Omit<CommissionDelivery, 'id' | 'created_at'>): Promise<CommissionDelivery | null> => {
  try {
    // Ensure revision_requested field exists with default value if not provided
    const deliveryData = {
      ...delivery,
      revision_requested: delivery.revision_requested ?? false
    };
    
    const { data, error } = await supabase
      .from('commission_deliveries')
      .insert(deliveryData)
      .select()
      .single();

    if (error) throw error;
    toast.success('Entrega realizada com sucesso');
    return data;
  } catch (error) {
    console.error('Error creating commission delivery:', error);
    toast.error('Erro ao registrar entrega');
    return null;
  }
};

export const updateCommissionDelivery = async (id: string, delivery: Partial<CommissionDelivery>): Promise<CommissionDelivery | null> => {
  try {
    const { data, error } = await supabase
      .from('commission_deliveries')
      .update(delivery)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error updating commission delivery:', error);
    toast.error('Erro ao atualizar entrega');
    return null;
  }
};
